import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

// Feature-scoped API module for consumer Brands — the per-merchant tags attached to
// products and branches that let one merchant's catalogue be served as several distinct
// Pickup Apps. Uses the shared axios instance (Bearer auth + 401 handling).
//
// Convention, matching vehicleApi.js: reads return data or a safe default and toast on
// error; mutations return the record or null and let the caller toast success.
//
// NOT to be confused with the pre-existing "brand" naming in this codebase — a merchant's
// `reference` (the tenant key) and a branch's `brandReference` (a POS/Deliverect slug) are
// unrelated to this entity.
//
// Ownership split, because Brands is half-synced from Odoo (GET /pos/api/brands):
//   Odoo owns  name          -> rewritten on every menu refresh, so editing it here is
//                               pointless for a synced row and the UI disables it
//   Admin owns active, arabicName, image, sortOrder, slug
// A brand is never deleted; `deactivateBrand` retires one and it can be switched back on.

// Public brand menu link on brandmenu-hub: <hub>/<merchant reference>/b/<slug>. It lists only
// the branches tagged with the brand and works only while the brand is active and has a slug.
// REACT_APP_MENU_HUB_URL points a staging panel at a staging hub; production needs nothing.
export const MENU_HUB_URL = String(process.env.REACT_APP_MENU_HUB_URL || "https://menu.wags.sa").replace(/\/+$/, "");

export const brandMenuUrl = (merchantReference, slug) =>
  `${MENU_HUB_URL}/${merchantReference}/b/${slug}`;

const read = async (url, params, fallback, label) => {
  try {
    const res = await axiosInstance.get(url, { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? `Error loading ${label}`);
    return fallback;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Error loading ${label}`);
    return fallback;
  }
};

const mutate = async (method, url, payload, label) => {
  try {
    const res = await axiosInstance[method](url, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? `Error ${label}`);
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Error ${label}`);
    return null;
  }
};

// Brands are merchant-scoped, so every call needs a merchant. A merchant-type user is
// pinned server-side from its JWT and merchantID is ignored; an admin must pass one.
export const getBrands = (merchantId, params = {}) =>
  read("/api/brands", { merchantID: merchantId, ...params }, [], "brands");

export const getBrand = (id) => read(`/api/brands/${id}`, {}, null, "brand");

// FormData when an image is attached, plain JSON otherwise — see toBrandFormData below.
export const createBrand = (payload) =>
  mutate("post", "/api/brands", payload, "creating brand");

export const updateBrand = (id, payload) =>
  mutate("put", `/api/brands/${id}`, payload, "updating brand");

// Brands are never removed from the database. This flips active to false, which drops the
// brand out of every app's filter while leaving its product/branch/app links intact so it
// can be switched back on. Reactivate with updateBrand(id, { active: true }).
export const deactivateBrand = (id) =>
  mutate("put", `/api/brands/${id}/deactivate`, {}, "deactivating brand");

export const deleteBrandImage = (id) =>
  mutate("delete", `/api/brands/${id}/image`, undefined, "removing brand image");

// The shared axios instance only sets multipart/form-data when the custom `rawRequest`
// flag is passed, and ImageController derives its upload directory from `dataType`, which
// multer reads as the fields stream in — so dataType MUST be appended before the file.
export const toBrandFormData = (fields, imageFile) => {
  const formData = new FormData();
  formData.append("dataType", "brand");
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    formData.append(key, value);
  });
  if (imageFile instanceof File) formData.append("image1", imageFile);
  return formData;
};

export const createBrandWithImage = async (formData) => {
  try {
    const res = await axiosInstance.post("/api/brands", formData, { rawRequest: true });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error creating brand");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error creating brand");
    return null;
  }
};

export const updateBrandWithImage = async (id, formData) => {
  try {
    const res = await axiosInstance.put(`/api/brands/${id}`, formData, { rawRequest: true });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating brand");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error updating brand");
    return null;
  }
};

// Source for the brand multi-selects on the app-edit and branch-edit screens. Only active
// brands are offered: appContext excludes inactive ones from an app's filter anyway, so
// offering them would imply an effect they don't have.
export const getActiveBrandOptions = async (merchantId) => {
  const brands = await getBrands(merchantId, { active: true });
  return (brands || []).map((b) => ({ value: b.id, label: b.name }));
};

// Attach a set of brands to an app (Apps <-> Brands). Mirrors the merchant allow-list
// endpoint; the app-edit form can also send brandIds inline with the rest of the payload.
export const updateAppBrands = (appId, brandIds) =>
  mutate("put", `/api/admin/apps/${appId}/brands`, { brandIds }, "updating app brands");

// A branch's current brand tags, for hydrating the branch-edit multi-select.
//
// Needs its own call because the branch list endpoints deliberately don't include brands —
// /api/branch/all is the public Pickup App payload and shouldn't carry them. The by-id
// endpoint does. Branch writes go through editBranch's FormData as a `brandIds` field.
export const getBranchBrandIds = async (branchId) => {
  const branch = await read(`/api/branch/${branchId}`, {}, null, "branch brands");
  return (branch?.brands || []).map((b) => b.id);
};
